import { type LibraryWithPermissions } from '@air/api/types';

import { reorderLibrariesByIds } from './reorderLibrariesByIds';

export type PageData = {
  data: LibraryWithPermissions[];
  [key: string]: unknown;
};

export type ReorderLibrariesAcrossPagesParams = {
  pages: PageData[];
  idsToMove: string[];
  betweenIds: {
    moveBeforeId?: string;
    moveAfterId?: string;
  };
};

/**
 * Reorders libraries across paginated data by flattening, reordering, and re-paginating.
 * Used for optimistic updates when drag-and-drop crosses page boundaries.
 *
 * The function:
 * 1. Flattens all pages into a single array
 * 2. Reorders using the idsToMove and betweenIds
 * 3. Re-paginates back into pages with the same sizes
 *
 * @param pages - Array of page objects containing library data
 * @param idsToMove - Array of library IDs to reposition
 * @param betweenIds - Target position IDs (moveBeforeId or moveAfterId)
 * @returns The updated pages array with reordered libraries
 *
 * @example
 * ```ts
 * const updatedPages = reorderLibrariesAcrossPages({
 *   pages: [
 *     { data: [lib1, lib2], hasMore: true },
 *     { data: [lib3, lib4], hasMore: false }
 *   ],
 *   idsToMove: ['lib2-id'],
 *   betweenIds: { moveAfterId: 'lib3-id' }
 * });
 * // Result:
 * // [
 * //   { data: [lib1, lib3], hasMore: true },
 * //   { data: [lib2, lib4], hasMore: false }
 * // ]
 * ```
 */
export const reorderLibrariesAcrossPages = ({
  pages,
  idsToMove,
  betweenIds,
}: ReorderLibrariesAcrossPagesParams): PageData[] => {
  // Step 1: Flatten all libraries from all pages
  const allLibraries = pages.flatMap((page) => page.data);

  // Step 2: Reorder the flattened array
  const reorderedLibraries = reorderLibrariesByIds({
    libraries: allLibraries,
    idsToMove,
    betweenIds,
  });

  // Step 3: Re-paginate back into pages with the same sizes
  let currentIndex = 0;
  const updatedPages = pages.map((page) => {
    const pageSize = page.data.length;
    const newPageData = reorderedLibraries.slice(currentIndex, currentIndex + pageSize);
    currentIndex += pageSize;

    return {
      ...page,
      data: newPageData,
    };
  });

  return updatedPages;
};
